
import * as _ from "lodash";

import { is } from "bpmn-js/lib/util/ModelUtil";
import { ValidationError, ValidationErrorType } from "../ValidationData";
import { ModdleElement } from "diagram-js/lib/model";

export enum ProcessMode {
    Process,
    SubProcess
}

export class FrendsRuleProvider {
    public static setProcessMode(mode: ProcessMode) {
        FrendsRuleProvider.processMode = mode;
    }

    public static getProcessMode() {
        return FrendsRuleProvider.processMode;
    }

    public static canCreateOrMove(shape: BpmnJsElement, target: BpmnJsElement): ValidationResult | void {
        if (FrendsRuleProvider.processMode == ProcessMode.SubProcess) {
            const rules = {} as any;
            rules.shouldHaveOneStartNode = FrendsRuleProvider.subprocessRootScopeShouldOnlyHaveOneStartNode(shape, target);
            rules.canNotHaveIntermediateReturn = FrendsRuleProvider.subprocessCanNotHaveIntermediateReturn(shape);
            for (const func in rules) {
                var result = rules[func] as ValidationResult;
                if (!result.valid) {
                    return result;
                }
            }
        }
        var result = FrendsRuleProvider.scopeShouldOnlyHaveOneStartNode(shape, target);
        if (!result.valid) {
            return result;
        }
    }

    public static canConnect(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement | null): ValidationResult | void {
        if (source && target && is(source as unknown as ModdleElement, "bpmn:FlowNode") && is(target as unknown as ModdleElement, "bpmn:FlowNode") && (target.type != "label" && source.type != "label")) //We only need to check rules if there is a target and a source. Flow node is inherited by all non-documentation nodes
        {
            const rules = {} as any;
            rules.cantConnectToSelf = FrendsRuleProvider.cantConnectToSelf;
            rules.preventDuplicating = FrendsRuleProvider.canNotDuplicateConnection;
            rules.preventLooping = FrendsRuleProvider.canNotCreateLoop;
            rules.limitGateway = FrendsRuleProvider.limitExclusiveGatewayConnection;
            rules.preventEndAndThrow = FrendsRuleProvider.preventEndAndThrowConnection;
            rules.preventCatchConnect = FrendsRuleProvider.canOnlyConnectCatchToValidTargets;
            rules.limitCatchOutgoing = FrendsRuleProvider.limitCatchOutgoing;
            rules.preventSignalEndEventConnect = FrendsRuleProvider.canOnlyConnectSignalEndEventToValidTargets;
            rules.triggersHaveToPointToSameNode = FrendsRuleProvider.triggersHaveToPointToSameNode;
            for (const func in rules) {
                const result = rules[func](source, target, connection) as ValidationResult;
                if (!result.valid) {
                    return result;
                }
            }
        }
    }

    public static cantConnectToSelf(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement): ValidationResult {
        if (source == target) {
            //This does need an error message, it should be clear it's not allowed.
            return { valid: false } as ValidationResult;
        }
        return { valid: true } as ValidationResult;
    }

    public static subprocessRootScopeShouldOnlyHaveOneStartNode(shape: BpmnJsElement, target: BpmnJsElement): ValidationResult {
        const result = { valid: true } as ValidationResult;
        if (shape && shape.type == "bpmn:StartEvent") {
            if (target && target.type == "bpmn:Process") {
                const existingShape = target.children?.find(c => c.type == "bpmn:StartEvent" && c.id != shape.id)
                if (existingShape) {
                    //No validation message wil lbe shown since the tooltip can't be anchored to an element
                    result.valid = false;
                }
            }
        }
        return result;
    }

    public static subprocessCanNotHaveIntermediateReturn(shape: BpmnJsElement) {
        const result = { valid: true } as ValidationResult;
        if (shape && shape.type == "bpmn:EndEvent" && shape.businessObject.eventDefinitions && shape.businessObject.eventDefinitions.some(x => x.$type == "bpmn:SignalEventDefinition")) {
            result.valid = false;
        }
        return result;
    }

    public static scopeShouldOnlyHaveOneStartNode(shape: BpmnJsElement, target: BpmnJsElement): ValidationResult {
        const result = { valid: true } as ValidationResult;
        if (shape && shape.type == "bpmn:StartEvent") {
            if (target && target.type == "bpmn:SubProcess") {
                const existingShape = target.children?.find(c => c.type == "bpmn:StartEvent" && c.id != shape.id)
                if (existingShape) {
                    result.message = FRENDS.Localizations.Validation_ScopeStartShape;
                    result.valid = false;
                }
            }
        }
        return result;
    }

    public static canNotDuplicateConnection(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement): ValidationResult {
        const result = { valid: true } as ValidationResult;
        if (source && target) {
            const filter = _.filter(source.outgoing, c => (c as any).target == target && c != connection);
            if (filter.length > 0) {
                result.message = FRENDS.Localizations.Validation_AlreadyConnected;
                result.valid = false;
            }
        }
        return result;
    }

    public static canNotCreateLoop(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement): ValidationResult {
        if (source && target && source !== target) {
            const visitedShapes = [];
            visitedShapes.push(source.id);
            const errors = FrendsRuleProvider.walkConnectionLoop(target, visitedShapes, connection != null ? connection.id : null);
            if (errors) {
                return {
                    valid: false,
                    message: FRENDS.Localizations.Validation_LoopsNotAllowed
                };
            }
        }
        return { valid: true, message: "" };
    }

    public static walkConnectionLoop(nextTarget: BpmnJsElement, visitedShapes: Array<string | undefined>, sourceConnectionId: string | null | undefined): ValidationError | null {
        // Each path has it's own list, therefore we need to create a new list instead of using the old one
        const localVisits = visitedShapes?.slice();
        if (nextTarget) {
            if (localVisits && localVisits.indexOf(nextTarget.id) > -1) {
                // It seems this path has encountered this target. It's a loop! D:
                return new ValidationError(ValidationErrorType.LoopsAreNotAllowed, nextTarget.id);
            }
            localVisits?.push(nextTarget.id);
            if (nextTarget.outgoing !== undefined) {
                for (const outgoing of nextTarget.outgoing) {
                    // In case we're moving a connection, don't walk the current connection. Also ignore DataInputAssociation connections
                    if (outgoing.id !== sourceConnectionId && outgoing.type !== "bpmn:DataInputAssociation") {
                        const error = FrendsRuleProvider.walkConnectionLoop(outgoing.target, localVisits, sourceConnectionId);
                        if (error) {
                            return error;
                        }
                    }
                }
            }
        }
        return null;
    }

    public static limitExclusiveGatewayConnection(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement): ValidationResult {
        const result = { valid: true } as ValidationResult;
        if (source && source.type == "bpmn:ExclusiveGateway") {
            if ((source.outgoing?.filter(x => x.type === "bpmn:SequenceFlow").length == 2 && !connection)) {
                result.message = FRENDS.Localizations.Validation_ExclusiveGatewayOutgoingLimit;
                result.valid = false;
            }
        }
        return result;
    }

    public static preventEndAndThrowConnection(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement): ValidationResult {
        const result = { valid: true } as ValidationResult;
        if (source && source.type == "bpmn:IntermediateThrowEvent" || source.type == "bpmn:EndEvent") {
            result.message = FRENDS.Localizations.Validation_NoOutgoingAllowed;
            result.valid = false;
        }
        return result;
    }

    public static limitCatchOutgoing(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement): ValidationResult {
        const result = { valid: true } as ValidationResult;
        if (source && source.type == "bpmn:IntermediateCatchEvent") {
            const sequendeFlows = source.outgoing?.filter(x => x.type === "bpmn:SequenceFlow");
            if (sequendeFlows !== undefined && sequendeFlows?.length > 0 && !(connection && connection.source == source)) {
                result.message = FRENDS.Localizations.Validation_OnlyOneOutgoing; //Only one outgoing connection is allowed from a catch. 
                result.valid = false;
            }

            if (!(target.type == "bpmn:Task" || target.type == "bpmn:ScriptTask" || target.type == "bpmn:SubProcess" || target.type == "bpmn:CallActivity")) {
                result.message = FRENDS.Localizations.Validation_InvalidErrorHandler;
                result.valid = false;
            }

        }
        return result;
    }


    public static triggersHaveToPointToSameNode(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement): ValidationResult {
        const result = { valid: true } as ValidationResult;
        if (source.type == "bpmn:StartEvent" && source.parent && source.parent.type == "bpmn:Process") {
            const process = source.parent;
            //Find potential existing trigger with outgoing connections. ignore own id: 
            const connectedTrigger = process.children?.find(c => c.type == "bpmn:StartEvent" && c.outgoing !== undefined && c.outgoing.filter(x => x.type === "bpmn:SequenceFlow").length > 0 && c.id != source.id);
            if (connectedTrigger) {
                //We can only connect to elements which are in the connected triggers outgoing list:
                if (!connectedTrigger.outgoing?.find(o => o.target.id == target.id)) {
                    result.valid = false;
                    result.message = FRENDS.Localizations.Validation_TriggersMustPointToSameTarget;
                }
            }
        }
        return result;
    }


    public static canOnlyConnectCatchToValidTargets(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement): ValidationResult {
        const result = { valid: true } as ValidationResult;

        if (target && target.type == "bpmn:IntermediateCatchEvent") {
            if (target.incoming !== undefined && target.incoming.length > 0 && !(connection && connection.target == target)) {
                result.valid = false;
                result.message = FRENDS.Localizations.Validation_OnlyOneIncoming; //only one incoming connection should be allowed for a catch shape
                return result;
            }
            //Only non-looping scopes, tasks and call activity should be able to have a catch attatched: 

            if (source && (source.type == "bpmn:SubProcess" || source.type == "bpmn:CallActivity" || source.type == "bpmn:Task")) {
                if (source.type == "bpmn:SubProcess" && source.businessObject && source.businessObject.loopCharacteristics) {
                    //loop characteristics is set, so catch connect should not be possible;
                    result.valid = false;
                    result.message = FRENDS.Localizations.Validation_ErrorHandlerNotAllowed;
                    return result;
                }
            }
            else {
                result.valid = false;
                result.message = FRENDS.Localizations.Validation_ErrorHandlerNotAllowed;
                return result;
            }


            if (source) {
                if (source.incoming !== undefined) {
                    for (const incoming of source.incoming) {
                        if (incoming.source && incoming.source.type == "bpmn:IntermediateCatchEvent") {
                            result.valid = false;
                            result.message = FRENDS.Localizations.Validation_ErrorHandlerCantBeCaught; //We don't allow catching a catched event
                            return result;
                        }
                    }
                }

                if (source.outgoing !== undefined) {
                    for (const outgoing of source.outgoing) {
                        if (outgoing.source && outgoing.target.type == "bpmn:IntermediateCatchEvent" && (connection == null || connection.id != outgoing.id)) {
                            result.valid = false;
                            result.message = FRENDS.Localizations.Validation_OnlyOneErrorHandler; //We don't allow multiple catches from the same source
                            return result;
                        }
                    }
                }
            }
        }
        return result;
    }

    public static canOnlyConnectSignalEndEventToValidTargets(source: BpmnJsElement, target: BpmnJsElement, connection?: BpmnJsConnectionElement): ValidationResult {
        const result = { valid: true } as ValidationResult;

        if (target && target.type == "bpmn:EndEvent" && target.businessObject.eventDefinitions && target.businessObject.eventDefinitions.some(x => x.$type == "bpmn:SignalEventDefinition")) {
            if (target.incoming !== undefined && target.incoming.length > 0 && !(connection && connection.target == target)) {
                result.valid = false;
                result.message = FRENDS.Localizations.Validation_OnlyOneIncoming; //only one incoming connection should be allowed for a signal end event
                return result;
            }

            //only one outgoing 
            if (source.outgoing !== undefined) {
                for (const outgoing of source.outgoing) {
                    if (outgoing.source && outgoing.target.type == "bpmn:EndEvent" && outgoing.target.businessObject.eventDefinitions && outgoing.target.businessObject.eventDefinitions.some(x => x.$type == "bpmn:SignalEventDefinition") && (connection == null || connection.id != outgoing.id)) {
                        result.valid = false;
                        result.message = FRENDS.Localizations.Validation_OnlyOneOutgoing; //We don't allow multiple intermediate returns from the same source
                        return result;
                    }
                }
            }
            //Only scripttask, tasks and call activity should be able to have a catch attatched: 
            if (source && (source.type != "bpmn:ScriptTask" && source.type != "bpmn:CallActivity" && source.type != "bpmn:Task")) {
                result.valid = false;
                result.message = FRENDS.Localizations.Validation_SignalEndEventNotAllowed;
                return result;
            }
        }
        return result;
    }

    private static processMode = ProcessMode.Process;

}

export interface ValidationResult {
    valid: boolean;
    message: string;
}
